import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
/**
 * ============================================================
 * KEJJA OS — src/pages/system/SystemPage.tsx
 * Single page with sections: Version, Activity, Integrations, Export, Danger.
 * Slate accent.
 * ============================================================
 */

import { useState } from 'react';
import InstallAppCard from '@/components/InstallAppCard';
import ThemePicker from '@/components/ThemePicker';
import {
  Settings2, Info, Activity as ActivityIcon, Download, Link2,
  AlertTriangle, Clock, CheckCircle2, XCircle, CircleDashed,
  Cpu, HardDrive, Trash2, LogOut, UserPlus,
} from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import {
  useVersion, useActivity, useIntegrations, useDeleteHome, useDeleteAccount,
  useLeaveHome,
} from '@/hooks/useSettings';
import { useTransferOwnership } from '@/hooks/useOffboarding';
import { systemApi, type IntegrationStatus } from '@/lib/settingsApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export default function SystemPage() {
  const { data: homesData } = useMyHomes();
  const first = homesData?.homes?.[0];
  const homeId = first?.home.id;
  const myRole = first?.role;
  const isAdmin = myRole === 'owner' || myRole === 'parent';

  if (!homeId) return <div className="text-sm text-muted-foreground">No home found.</div>;

  return (
    <div className="space-y-5 pb-12">
      <InstallAppCard />

      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <div>
          <div className="text-sm font-semibold">Appearance</div>
          <p className="text-[11px] text-muted-foreground">Choose light, dark, or match your device.</p>
        </div>
        <ThemePicker />
      </div>

      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 flex items-center justify-center flex-shrink-0">
            <Settings2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold leading-tight tracking-tight">System</h1>
            <p className="text-xs text-muted-foreground mt-0.5 italic">
              Under the hood.
            </p>
          </div>
        </div>
      </header>

      <VersionSection />
      <IntegrationsSection homeId={homeId} />
      <ActivitySection homeId={homeId} />
      <ExportSection homeId={homeId} isAdmin={isAdmin} />
      <DangerSection homeId={homeId} homeName={first.home.name} isAdmin={isAdmin} />
    </div>
  );
}

// ============================================================
// Version
// ============================================================

function VersionSection() {
  const { data, isLoading } = useVersion();

  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <Info className="w-3 h-3" /> Version
      </h2>
      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <Skeleton className="h-16" />
          ) : data ? (
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-muted-foreground">App</div>
                <div className="font-semibold">{data.app.name} v{data.app.version}</div>
              </div>
              <div>
                <div className="text-muted-foreground flex items-center gap-1">
                  <Cpu className="w-2.5 h-2.5" /> Runtime
                </div>
                <div className="font-semibold font-mono">{data.backend.node} · {data.backend.platform}</div>
              </div>
              <div>
                <div className="text-muted-foreground flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" /> Uptime
                </div>
                <div className="font-semibold">{formatUptime(data.runtime.uptimeSeconds)}</div>
              </div>
              <div>
                <div className="text-muted-foreground flex items-center gap-1">
                  <HardDrive className="w-2.5 h-2.5" /> Memory
                </div>
                <div className="font-semibold">{data.runtime.memoryMB} MB</div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Could not load version.</div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function formatUptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}

// ============================================================
// Integrations
// ============================================================

function IntegrationsSection({ homeId }: { homeId: string }) {
  const { data, isLoading } = useIntegrations(homeId);
  const integrations = data?.integrations ?? [];

  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <Link2 className="w-3 h-3" /> Integrations
      </h2>
      <Card>
        <CardContent className="p-0 divide-y divide-border">
          {isLoading ? (
            <div className="p-4 space-y-2"><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
          ) : (
            integrations.map((i) => <IntegrationRow key={i.id} integration={i} />)
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function IntegrationRow({ integration }: { integration: IntegrationStatus }) {
  const { icon: Icon, color } = statusDisplay(integration.status);
  return (
    <div className="p-3 flex items-start gap-3">
      <div className={cn('mt-0.5 flex-shrink-0', color)}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">{integration.name}</span>
          <Badge variant="outline" className={cn('text-[9px] h-4 px-1 capitalize', color)}>
            {integration.status}
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground">{integration.description}</p>
        {integration.detail && (
          <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{integration.detail}</p>
        )}
      </div>
    </div>
  );
}

function statusDisplay(status: IntegrationStatus['status']) {
  switch (status) {
    case 'active': return { icon: CheckCircle2, color: 'text-emerald-600' };
    case 'inactive': return { icon: CircleDashed, color: 'text-muted-foreground' };
    case 'error': return { icon: XCircle, color: 'text-rose-600' };
    case 'planned': return { icon: Clock, color: 'text-amber-600' };
  }
}

// ============================================================
// Activity
// ============================================================

function ActivitySection({ homeId }: { homeId: string }) {
  const { data, isLoading } = useActivity(homeId, 50);
  const activity = data?.activity ?? [];

  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <ActivityIcon className="w-3 h-3" /> Recent activity
      </h2>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4"><Skeleton className="h-20" /></div>
          ) : activity.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground italic">
              No recent activity logged yet. This will fill as the household uses Kejja.
            </div>
          ) : (
            <div className="divide-y divide-border max-h-80 overflow-y-auto">
              {activity.map((a) => (
                <div key={a.id} className="p-3 flex items-start gap-3 text-xs">
                  <div className="flex-shrink-0 w-16">
                    <Badge variant="outline" className="text-[9px] capitalize">{a.module}</Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="capitalize font-medium">{a.action}</span>
                      {a.entityType && <span className="text-muted-foreground"> · {a.entityType}</span>}
                    </div>
                    {a.summary && <p className="text-[11px] text-muted-foreground truncate">{a.summary}</p>}
                  </div>
                  <span className="text-[10px] text-muted-foreground flex-shrink-0">
                    {new Date(a.createdAt).toLocaleString('en-KE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ============================================================
// Export
// ============================================================

function ExportSection({ homeId, isAdmin }: { homeId: string; isAdmin: boolean }) {
  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <Download className="w-3 h-3" /> Data export
      </h2>
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Download a full JSON snapshot of your household's data across every module.
            Useful for backup or moving to another instance.
          </p>
          {isAdmin ? (
            <a
              href={systemApi.exportUrl(homeId)}
              download
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium"
            >
              <Download className="w-3.5 h-3.5" /> Download full export
            </a>
          ) : (
            <p className="text-[11px] text-amber-700 dark:text-amber-300">Only admins can export household data.</p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ============================================================
// Danger zone
// ============================================================

function DangerSection({ homeId, homeName, isAdmin }: {
  homeId: string; homeName: string; isAdmin: boolean;
}) {
  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-rose-600 mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3" /> Danger zone
      </h2>
      <Card className="border-rose-300/60 dark:border-rose-900/40">
        <CardContent className="p-4 space-y-4">
          <LeaveHomeRow homeId={homeId} homeName={homeName} />
          {isAdmin && <TransferOwnershipRow homeId={homeId} homeName={homeName} />}
          {isAdmin && (
            <DeleteHomeRow homeId={homeId} homeName={homeName} />
          )}
          <DeleteAccountRow />
        </CardContent>
      </Card>
    </section>
  );
}

function DeleteHomeRow({ homeId, homeName }: { homeId: string; homeName: string }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [typedName, setTypedName] = useState('');
  const deleteMut = useDeleteHome(homeId);

  const handleDelete = () => {
    deleteMut.mutate(undefined, {
      onSuccess: () => { window.location.href = '/'; },
      onError: (e) => alert(e.message ?? 'Could not delete'),
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">Delete this home</div>
          <p className="text-[11px] text-muted-foreground">
            Soft-deletes <span className="font-mono">{homeName}</span> and deactivates all memberships.
            Data becomes inaccessible but is retained for 30 days before hard deletion.
          </p>
        </div>
        {!showConfirm && (
          <Button
            size="sm" variant="outline"
            className="text-rose-600 border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/20"
            onClick={() => setShowConfirm(true)}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
        )}
      </div>

      {showConfirm && (
        <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-300/60 dark:border-rose-900/40 rounded-md p-3 space-y-2">
          <p className="text-xs">
            Type <span className="font-mono font-bold">{homeName}</span> to confirm:
          </p>
          <Input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="flex gap-2">
            <Button
              size="sm" variant="ghost"
              onClick={() => { setShowConfirm(false); setTypedName(''); }}
            >
              Cancel
            </Button>
            <Button
              size="sm" className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={typedName !== homeName || deleteMut.isPending}
              onClick={handleDelete}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Confirm delete'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteAccountRow() {
  const [showConfirm, setShowConfirm] = useState(false);
  const [typed, setTyped] = useState('');
  const deleteMut = useDeleteAccount();

  const handleDelete = () => {
    deleteMut.mutate(undefined, {
      onSuccess: () => { window.location.href = '/'; },
      onError: (e) => alert(e.message ?? 'Could not delete'),
    });
  };

  return (
    <div className="space-y-2 pt-4 border-t border-rose-300/30 dark:border-rose-900/20">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">Delete my account</div>
          <p className="text-[11px] text-muted-foreground">
            Removes you from all households and soft-deletes your user account.
          </p>
        </div>
        {!showConfirm && (
          <Button
            size="sm" variant="outline"
            className="text-rose-600 border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/20"
            onClick={() => setShowConfirm(true)}
          >
            <LogOut className="w-3.5 h-3.5 mr-1" /> Delete account
          </Button>
        )}
      </div>

      {showConfirm && (
        <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-300/60 dark:border-rose-900/40 rounded-md p-3 space-y-2">
          <p className="text-xs">Type <span className="font-mono font-bold">delete my account</span> to confirm:</p>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value.toLowerCase())}
            className="h-8 text-xs"
          />
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setShowConfirm(false); setTyped(''); }}>
              Cancel
            </Button>
            <Button
              size="sm" className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={typed !== 'delete my account' || deleteMut.isPending}
              onClick={handleDelete}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Confirm delete'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Leave home
// ============================================================
function LeaveHomeRow({ homeId, homeName }: { homeId: string; homeName: string }) {
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();
  const leaveMut = useLeaveHome(homeId);

  const onLeave = () => {
    leaveMut.mutate(undefined, {
      onSuccess: () => navigate('/onboarding', { replace: true }),
    });
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Leave "{homeName}"</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            You'll lose access to shared data. Other members stay. If you're the only owner, transfer ownership first.
          </div>
        </div>
        {!confirming ? (
          <Button size="sm" variant="outline"
            className="border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30"
            onClick={() => setConfirming(true)}>
            <LogOut className="w-3 h-3 mr-1" /> Leave
          </Button>
        ) : null}
      </div>
      {confirming && (
        <div className="mt-3 bg-rose-50 dark:bg-rose-950/20 border border-rose-300/60 dark:border-rose-900/40 rounded-md p-3 space-y-2">
          {leaveMut.isError && (
            <div className="text-xs text-rose-700 dark:text-rose-300">
              {(leaveMut.error as Error)?.message ?? 'Failed to leave'}
            </div>
          )}
          <div className="text-xs">Confirm you want to leave this home.</div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={onLeave} disabled={leaveMut.isPending}>
              {leaveMut.isPending ? 'Leaving…' : 'Confirm leave'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Transfer ownership
// ============================================================
function TransferOwnershipRow({ homeId, homeName }: { homeId: string; homeName: string }) {
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState('');
  const xferMut = useTransferOwnership();

  const { data: members = [] } = useQuery<Array<{ userId: string; email: string; displayName: string; role: string; isActive: boolean }>>({
    queryKey: ['home', homeId, 'members'],
    queryFn: async () => {
      try { return await api.get(`/homes/${homeId}/members`) as any; }
      catch { return []; }
    },
    enabled: picking,
  });

  const eligible = members.filter((m) => m.isActive && m.role !== 'owner');

  const onTransfer = () => {
    if (!target) return;
    const t = eligible.find((m) => m.userId === target);
    if (!t) return;
    if (!confirm(`Transfer ownership of "${homeName}" to ${t.displayName}? You will become a parent.`)) return;
    xferMut.mutate({ homeId, toUserId: target }, {
      onSuccess: () => { setPicking(false); setTarget(''); },
    });
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Transfer ownership</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Hand "{homeName}" over to another member. You will become a parent.
          </div>
        </div>
        {!picking && (
          <Button size="sm" variant="outline" onClick={() => setPicking(true)}
            className="border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30">
            <UserPlus className="w-3 h-3 mr-1" /> Transfer
          </Button>
        )}
      </div>
      {picking && (
        <div className="mt-3 bg-rose-50 dark:bg-rose-950/20 border border-rose-300/60 dark:border-rose-900/40 rounded-md p-3 space-y-2">
          {xferMut.isError && (
            <div className="text-xs text-rose-700 dark:text-rose-300">
              {(xferMut.error as Error)?.message ?? 'Transfer failed'}
            </div>
          )}
          <select value={target} onChange={(e) => setTarget(e.target.value)}
            className="w-full h-9 px-2 rounded-md bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 text-sm">
            <option value="">— choose a member —</option>
            {eligible.map((m) => (
              <option key={m.userId} value={m.userId}>{m.displayName} · {m.role}</option>
            ))}
          </select>
          {eligible.length === 0 && (
            <div className="text-xs text-muted-foreground">Invite or wait for another active member to join before transferring.</div>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setPicking(false); setTarget(''); }}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" disabled={!target || xferMut.isPending} onClick={onTransfer}>
              {xferMut.isPending ? 'Transferring…' : 'Confirm transfer'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
